import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Award, Target, Users, Puzzle, Calendar, Sparkles, Star } from 'lucide-react';
import { useAchievements } from '@/hooks/useAchievements';
import { AchievementCard } from '@/components/AchievementCard';
import { cn } from '@/lib/utils';

const categoryIcons = {
  games: Trophy,
  elo: Target,
  puzzles: Puzzle,
  social: Users,
  streaks: Calendar,
  tutorials: Award,
  special: Sparkles
};

const categoryLabels = {
  games: 'Games',
  elo: 'ELO',
  puzzles: 'Puzzles',
  social: 'Social',
  streaks: 'Streaks',
  tutorials: 'Tutorials',
  special: 'Special'
};

export function AchievementsPanel() {
  const { 
    achievements, 
    userProgress, 
    summary, 
    loading,
    getProgress,
    getProgressPercentage 
  } = useAchievements();
  
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [filterType, setFilterType] = useState<'all' | 'completed' | 'in-progress' | 'locked'>('all');

  // Group achievements by category
  const achievementsByCategory = useMemo(() => {
    const grouped: Record<string, typeof achievements> = {
      all: achievements
    };

    achievements.forEach(achievement => {
      if (!grouped[achievement.category]) {
        grouped[achievement.category] = [];
      }
      grouped[achievement.category].push(achievement);
    });

    return grouped;
  }, [achievements]);

  // Filter achievements
  const filteredAchievements = useMemo(() => {
    let filtered = selectedCategory === 'all' 
      ? achievements 
      : achievementsByCategory[selectedCategory] || [];

    if (filterType === 'completed') {
      filtered = filtered.filter(a => getProgress(a.id)?.completed);
    } else if (filterType === 'in-progress') {
      filtered = filtered.filter(a => {
        const progress = getProgress(a.id);
        return progress && !progress.completed && progress.current_progress > 0;
      });
    } else if (filterType === 'locked') {
      filtered = filtered.filter(a => !getProgress(a.id) || getProgress(a.id)?.current_progress === 0);
    }

    return filtered;
  }, [achievements, achievementsByCategory, selectedCategory, filterType, getProgress]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full mt-2" />
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Achievements & Milestones
            </CardTitle>
            <CardDescription>Track your progress and unlock rewards</CardDescription>
          </div>

          {summary && (
            <div className="flex items-center gap-2">
              <Badge variant="default" className="text-base px-4 py-2">
                <Star className="w-4 h-4 mr-2" />
                {summary.total_points} pts
              </Badge>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {summary.earned_achievements} / {summary.total_achievements} achievements unlocked
              </span>
              <span className="font-semibold text-primary">
                {summary.completion_percentage}%
              </span>
            </div>
            <Progress value={summary.completion_percentage} className="h-3" />
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <Select value={filterType} onValueChange={(v) => setFilterType(v as typeof filterType)}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filter by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Achievements</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
          <TabsList className="w-full flex-wrap h-auto gap-2 bg-transparent">
            <TabsTrigger value="all" className="gap-2">
              All
            </TabsTrigger>
            {Object.entries(categoryLabels).map(([key, label]) => {
              const Icon = categoryIcons[key as keyof typeof categoryIcons];
              const count = achievementsByCategory[key]?.length || 0;
              const completed = achievementsByCategory[key]?.filter(a => 
                getProgress(a.id)?.completed
              ).length || 0;

              return (
                <TabsTrigger key={key} value={key} className="gap-2">
                  <Icon className="w-4 h-4" />
                  {label}
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {completed}/{count}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={selectedCategory} className="mt-6">
            {filteredAchievements.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No achievements found in this category</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredAchievements.map((achievement) => (
                  <AchievementCard
                    key={achievement.id}
                    achievement={achievement}
                    progress={getProgress(achievement.id)}
                    progressPercentage={getProgressPercentage(achievement.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
